/**
 * [Servexa] What to tell a Company Admin / Sub Admin when a Support ticket or a
 * Feature suggestion was saved but NOT yet sent to Servexa (spec A12/A22:
 * "submission not lost, sane retry, honest UI state").
 *
 * The two unsent states mean different things and must not share wording:
 *   - `pending_sync` — queued; the server's retry runner sends it automatically
 *     (e.g. Servexa unreachable, busy, under maintenance, or the connection is
 *     waiting for re-approval). Nothing for the user to do.
 *   - `failed`       — the runner will NOT try again on its own (Servexa could
 *     not accept it, or the feature module is switched off). Only the Retry
 *     button resends it.
 *
 * `reason` is the server's plain-language `lastSyncError` (or `error` on a
 * create/retry response) — already safe to show; never Servexa's raw text.
 */

/** @returns {'queued'|'failed'|null} */
export function unsentKind(record) {
  if (record?.syncState === 'failed') return 'failed'
  if (record?.syncState === 'pending_sync') return 'queued'
  return null
}

/**
 * Toast after a create / retry response that was not sent.
 * @param {object} result  the API result (`syncState`, `error`, `lastSyncError`)
 * @param {string} noun    'Ticket' | 'Suggestion'
 * @returns {{ tone: 'info'|'warning', text: string }}
 */
export function notSentNotice(result, noun) {
  const reason = result?.error || result?.lastSyncError || null
  if (unsentKind(result) === 'failed') {
    return {
      tone: 'warning',
      text: `${noun} saved, but it could not be sent to support. ${reason ?? 'Open it to retry.'}`,
    }
  }
  return {
    tone: 'info',
    text: reason ? `${noun} saved. ${reason}` : `${noun} saved — it will be sent to support automatically.`,
  }
}

/**
 * Heading + body for the detail page's "not sent" banner.
 * @param {object} record  the stored record (`syncState`, `lastSyncError`)
 * @returns {{ title: string, body: string }}
 */
export function notSentBanner(record) {
  const reason = record?.lastSyncError || null
  if (unsentKind(record) === 'failed') {
    return {
      title: 'Could not be sent to support',
      body: reason ?? 'It was saved but could not be sent. Use Retry to send it again.',
    }
  }
  return {
    title: 'Waiting to be sent to support',
    body: reason ?? 'It is saved and will be sent automatically. You can also retry now.',
  }
}
